<template>
  <div class="full-h-view d-flex flex-column justify-content-between h-100">
    <div
      class="nav-header d-flex justify-content-between align-items-center py-1 ps-3 pe-1"
    >
      <h1 class="add_on-confirmation-title">{{ t("ADD_ONS.CONFIRMATION") }}</h1>
      <span
        class="material-symbols-outlined p-2_5 close-view"
        @click="handleClose"
        >close</span
      >
    </div>
    <div class="content-container">
      <div
        class="mx-4 d-flex flex-grow-1 flex-column justify-content-center align-items-center text-center"
      >
        <div class="flex-grow-1"></div>
        <div class="mb-2">
          <dotlottie-wc
            :src="`${useAsset('img/add-on/confirmation-anim.lottie')}`"
            autoplay="true"
            loop="true"
          ></dotlottie-wc>
        </div>
        <div class="notif-msg mb-2">{{ t("ADD_ONS.SERVICE_ACTIVATED") }}</div>
        <div style="margin-bottom: 3rem" class="notif-content">
          {{ confirmationMessage }}
        </div>
        <div v-if="nextPayment" class="next-payment-info mb-3">
          {{
            t("ADD_ONS.NEW_PAYMENT_MESSAGE", {
              date: formatDate(nextPayment.dueDate),
              amount: nextPayment.minimumPaymentAmount,
            })
          }}
        </div>
        <div class="flex-grow-1"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import "@lottiefiles/dotlottie-wc";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { shortDateFormatHelper } from "@/apps/credit-line/helpers/dateFormat";
import type { IAddOnDetailsResponse } from "@/apps/credit-line/types/responses";
import type { IPaymentSchedule } from "@/apps/credit-line/models/CreditLineDetails";
import { isTelcoProviderType } from "@/apps/credit-line/enums/addOnProviders";

interface Props {
	addOnDetails: IAddOnDetailsResponse | null;
	onClose: () => void;
}

const props = defineProps<Props>();

const { fetchSummary } = useCreditLineSummary();
const { t, locale } = useI18n();

const nextPayment = ref<IPaymentSchedule | null>(null);

const confirmationMessage = computed(() => {
	const providerType = props.addOnDetails?.addOnProviderType || "Unknown";
	if (isTelcoProviderType(providerType)) {
		return t("ADD_ONS.CONFIRMATION_MESSAGE_TELCO", {
			addOnName: props.addOnDetails?.addOnName || "",
			provider: props.addOnDetails?.addOnProvider || "",
		});
	}
	return t("ADD_ONS.CONFIRMATION_MESSAGE");
});

onMounted(async () => {
	logAnalyticsEvent(
		AnalyticEvents.CreditLine.AddOnConfirmationPageDisplayed,
		"AddOns",
		undefined,
		{
			cartAddOnId: props.addOnDetails?.cartAddOnId,
			addOnName: props.addOnDetails?.addOnName,
		}
	);
	getSummary();
});

const handleClose = () => {
	logAnalyticsEvent(
		AnalyticEvents.CreditLine.AddOnConfirmationCloseClicked,
		"AddOns"
	);
	props.onClose();
};

const formatDate = (epochTime: number): string => {
	const date = new Date(epochTime * 1000);
	return shortDateFormatHelper(date.toISOString(), locale.value);
};

const getSummary = () => {
	fetchSummary(true)
		.then((summary) => {
			if (summary?.paymentSchedule?.length > 0) {
				const sortedPayments = [...summary.paymentSchedule].sort(
					(a, b) => a.payment - b.payment
				);
				nextPayment.value = sortedPayments[0];
			}
		})
		.catch((error) => {
			logAnalyticsEvent(
				AnalyticEvents.CreditLine.AddOnSummaryLoadError,
				"AddOns",
				undefined,
				{
					error: error?.message || "Unknown error",
					errorCode: error?.response?.data?.errorCode || "UNKNOWN",
				}
			);
		});
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_spacing-utils.scss";

.full-h-view {
  min-height: 100dvh;
}
.nav-header {
  border-bottom: 1px solid #eee;
}
.add_on-confirmation-title {
  font-size: 1rem;
  font-weight: 500;
  font-family: Inter;
  line-height: 1.5rem;
  margin-bottom: 0;
  color: #111;
}
.close-view {
  color: #1c1b1f;
}
.notif-msg {
  font-size: 20px;
  line-height: 29px;
  font-weight: 600;
  font-family: Inter;
}
.notif-content {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  font-family: Inter;
  color: #595a5a;
}
.next-payment-info {
  max-width: 280px;
  min-height: 40px;
  line-height: 20px;
  font-size: 0.875rem;
  font-weight: 400;
  font-family: Inter;
  color: #8b8b8b;
}
.content-container {
  position: fixed;
  top: 3.5rem;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
}
</style>
